import type { CatalogIndex } from '../../catalog/types'
import {
  computeAll,
  computeBalance,
  type Balance,
  type LineValue,
} from '../../domain/balance'
import type { Quest } from '../../domain/types'

export function BalancePanel({ quest, catalog }: { quest: Quest; catalog: CatalogIndex }) {
  const balance = computeBalance(quest, catalog)
  const computed = computeAll(quest, catalog)
  const stored = quest.computed

  // The file's own computed block is Unity's last word. A gap means the file is
  // stale relative to these edits — expected while editing, worth seeing.
  const drifted =
    stored.estimatedValue !== computed.estimatedValue ||
    stored.actualValue !== computed.actualValue ||
    stored.gatingLevel !== computed.gatingLevel

  return (
    <aside className="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-l border-edge bg-ink-deep p-4">
      <div>
        <h3 className="text-sm font-semibold text-gold">Balance</h3>
        <p className="mt-0.5 text-xs text-dim">
          Recomputed as you type, using the game's own formula.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Figure label="Estimated" value={balance.estimatedValue} hint="what it takes" />
        <Figure label="Actual" value={balance.actualValue} hint="what it pays" />
      </div>

      <RatioBar balance={balance} />

      <LineTable title="Requirements — consumed" lines={balance.requirementLines} gold={quest.goldRequirement} />
      {balance.showLines.length > 0 && (
        <LineTable
          title="To show — not counted"
          lines={balance.showLines}
          gold={quest.goldRequirementToShow}
          muted
        />
      )}
      <LineTable title="Rewards" lines={balance.rewardLines} gold={quest.goldReward} />

      <div className="rounded border border-edge bg-ink-lift p-2 text-xs">
        <div className="mb-1 font-semibold text-parchment">Gating</div>
        <Row label="gatingLevel" value={String(computed.gatingLevel)} />
        <Row label="requirementLevels" value={computed.requirementLevels} />
        <p className="mt-1.5 text-[11px] leading-snug text-dim">
          The renown level where this quest first becomes completable. The in-game list
          sorts by it.
        </p>
      </div>

      {drifted && (
        <p className="rounded border border-warn-edge bg-warn-bg p-2 text-[11px] leading-snug text-warn">
          The <code>computed</code> block in the file still reads {stored.estimatedValue}/
          {stored.actualValue}. Saving writes the fresh values, so Unity's next export
          produces no diff here.
        </p>
      )}
    </aside>
  )
}

function Figure({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="rounded border border-edge bg-ink-lift p-2">
      <div className="text-xs uppercase tracking-wide text-dim">{label}</div>
      <div className="font-mono text-2xl tabular-nums text-parchment">{value}</div>
      <div className="text-[11px] text-dim">{hint}</div>
    </div>
  )
}

function RatioBar({ balance }: { balance: Balance }) {
  if (balance.ratio === null) {
    return (
      <div className="rounded border border-edge bg-ink-lift p-2 text-xs text-dim">
        No ratio — this quest takes nothing, so there's nothing to divide by.
      </div>
    )
  }
  const percent = Math.round(balance.ratio * 100)
  // Under 100% the quest runs at a loss to the player, which is normal for main-line
  // progression. Colour is a cue to look, not a verdict.
  const tone =
    percent < 60
      ? 'bg-danger'
      : percent < 100
        ? 'bg-warn'
        : percent <= 140
          ? 'bg-ok'
          : 'bg-royal-lit'
  return (
    <div className="rounded border border-edge bg-ink-lift p-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-dim">Pays / takes</span>
        <span className="font-mono text-lg tabular-nums text-parchment">{percent}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded bg-edge">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(percent, 200) / 2}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-dim">
        {percent < 100
          ? 'Runs at a loss — normal for main-line quests.'
          : 'Pays out more than it takes.'}
      </div>
    </div>
  )
}

function LineTable({
  title,
  lines,
  gold,
  muted = false,
}: {
  title: string
  lines: LineValue[]
  gold: number
  muted?: boolean
}) {
  if (lines.length === 0 && gold === 0) return null
  return (
    <div className={`rounded border border-edge bg-ink-lift p-2 text-xs ${muted ? 'opacity-70' : ''}`}>
      <div className="mb-1 font-semibold text-parchment">{title}</div>
      {lines.map((line, index) => (
        <div key={index} className="flex items-baseline justify-between gap-2 py-0.5">
          <span className="min-w-0 flex-1 truncate font-mono text-dim">
            {line.ref.item || <em className="text-dim">empty</em>}
            {line.ref.amount > 1 && <span className="text-dim"> ×{line.ref.amount}</span>}
          </span>
          {line.missing ? (
            <span className="shrink-0 font-semibold text-danger">?</span>
          ) : (
            <span className="shrink-0 font-mono tabular-nums text-gold">
              {line.subtotal}
              <span className="ml-1 text-[10px] text-dim">@{line.unit}</span>
            </span>
          )}
        </div>
      ))}
      {gold > 0 && (
        <div className="flex items-baseline justify-between gap-2 border-t border-edge py-0.5">
          <span className="text-dim">gold</span>
          <span className="font-mono tabular-nums text-gold">{gold}</span>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="font-mono text-dim">{label}</span>
      <span className="font-mono tabular-nums text-gold">{value}</span>
    </div>
  )
}
